// @flow
import React, { Component } from 'react';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { copyToClipboard } from '../../../../utils/clipboard';
import { Default, Mobile } from '../../../../utils/responsive';
import classNames from 'classnames/bind';
import environment from '../../../../environment';
import PropTypes from 'prop-types';
import Modal from '../../../../elements/Modal/Modal';
import RadioButton from '../../../../elements/RadioButton/RadioButton';

import style from './ShareListModal.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import icons from '../../../../assets/styles/icons.scss';
import imageType1 from '../../../../assets/img/wish-list/Modal/tipo1.png';
import imageType2 from '../../../../assets/img/wish-list/Modal/tipo2.png';
import imageType3 from '../../../../assets/img/wish-list/Modal/tipo3.png';

import type { LocaleState } from '../../../locale/duck';
import type { Location } from '../../../../router';
import type { WishList } from '../../../../entities';

type WishListType = {
  type: number;
  image: string;
  width: number;
  height: number;
};

type Props = {
  +openModal?: boolean;
  +handleClose: () => void;
  +localeState: LocaleState;
  +wishList?: WishList;
};

type State = {
  wishListTypeIndex: number;
  copyUrlClicked: boolean;
  copyIframeClicked: boolean;
};

const messages = defineMessages({
  description: {
    id: 'wishList.document.description',
  },
});

const wishListTypes: WishListType[] = [
  {
    type: 1,
    image: imageType1,
    width: 186,
    height: 594,
  },
  {
    type: 2,
    image: imageType2,
    width: 704,
    height: 258,
  },
  {
    type: 3,
    image: imageType3,
    width: 200,
    height: 234,
  },
];

const buildPublicUrl = (wishList: WishList) =>
  `https://www.qempo.com/wish-list/${wishList.user.id}/${wishList.slug}`;

const buildMessage = (wishList: WishList, formatMessage: ({}) => string) =>
  `${formatMessage(messages.description)} ${buildPublicUrl(wishList)}`;

export default class ShareListModal extends Component<Props, State> {
  state = {
    wishListTypeIndex: 0,
    copyUrlClicked: false,
    copyIframeClicked: false,
  };

  handleTypeChange = (index: number) => {
    this.setState({
      wishListTypeIndex: index,
    });
  };

  handleClickFBSharedDialog = () => {
    const { wishList } = this.props;
    const shareUrl = buildPublicUrl(wishList);
    if (!(window: any).FB) return;
    (window: any).FB.ui({
      method: 'share',
      href: shareUrl,
    }, (response) => console.log(response));
  };

  handleClickTwitterShare = () => {
    const { wishList } = this.props;
    const message = buildMessage(wishList, this.context.intl.formatMessage);
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`;
    const width = 550;
    const height = 500;
    const options = `height=${height},width=${width},resizable=yes,toolbar=no,menubar=no,location=no,directories=no,status=yes`;
    window.open(url, 'popUpWindow', options);
  };

  handleCopyUrl = (urlRedirect: string) => {
    copyToClipboard(urlRedirect);
    this.setState({ copyUrlClicked: true });
  };

  handleCopyIframe = (iframe: string) => {
    copyToClipboard(iframe);
    this.setState({ copyIframeClicked: true });
  };

  buildIframeUrlFn = () => {
    const { wishListTypeIndex } = this.state;
    const { wishList } = this.props;
    return wishList && `${environment.share_url}/wish-list/${wishList.user.id}/${wishList.slug}/${wishListTypes[wishListTypeIndex].type}`
  };

  render() {
    const { wishListTypeIndex, copyUrlClicked, copyIframeClicked } = this.state;
    const { openModal, handleClose, wishList, localeState } = this.props;
    const urlRedirect = !!wishList &&
      `${environment.baseUrl[localeState.domainCountry || 'US']}/wish-list/${wishList.user.id}/${wishList.slug}`;
    const buildIframeUrl = this.buildIframeUrlFn();
    const buttonCopyUrlIntl = copyUrlClicked ? 'wishList.modal.copy.button.copied' : 'wishList.options.copyUrl';
    const buttonCopyIframeIntl = copyIframeClicked ? 'wishList.modal.copy.button.copied' : 'wishList.modal.copy.button';

    const iframe = `<iframe src="${buildIframeUrl || ''}" 
                            style="overflow: hidden; border: none;" 
                            height="${wishListTypes[wishListTypeIndex].height}" 
                            width="${wishListTypes[wishListTypeIndex].width}"></iframe>`;

    const options = (<section className={style.options}>
      <FormattedMessage id="wishList.modal.type.label">{
        text => <label>{text}</label>
      }</FormattedMessage>
      {wishListTypes.map((w: WishListType, index: number) => (
        <article className={classNames(style.type, { [style.selected]: wishListTypeIndex === index })}
                 key={index}
                 onClick={() => this.handleTypeChange(index)}>
          <RadioButton name="method"
                       value={index}
                       selected={wishListTypeIndex}
                       handleChange={_ => {
                       }}>
            <FormattedMessage id="wishList.search.type.label">{
              txt => <span className={style.buttonLabel}>{`${txt} ${index + 1}`}</span>
            }</FormattedMessage>
          </RadioButton>
        </article>))}
    </section>);
    return (
      <Modal open={openModal}
             width={624}
             height={808}
             handleClose={handleClose}>
        <section className={style.shareListWrap}>
          <FormattedMessage id="wishList.modal.title">{
            text => <h2>{text}</h2>
          }</FormattedMessage>
          <FormattedMessage id="wishList.modal.subtitle.one">{
            text => <p className={style.subtitle}>{text}</p>
          }</FormattedMessage>
          <section className={style.socialsWrap}>
            <section className={style.facebook}
                     onClick={_ => this.handleClickFBSharedDialog()}>
              <i className={icons.symbolFacebook}/>
              <FormattedMessage id="wishList.options.facebook">{
                text => <p>{text}</p>
              }</FormattedMessage>
            </section>
            <section className={style.twitter}
                     onClick={_ => this.handleClickTwitterShare()}>
              <i className={icons.symbolTwitter}/>
              <p>Tweet</p>
            </section>
            {!!urlRedirect && <section className={style.url}
                                       onClick={_ => this.handleCopyUrl(urlRedirect)}>
              <FormattedMessage id={buttonCopyUrlIntl}>{
                text => <p>{text}</p>
              }</FormattedMessage>
            </section>}
          </section>
          <FormattedMessage id="wishList.modal.subtitle.two">{
            text => <p className={style.subtitle}>{text}</p>
          }</FormattedMessage>
          <section className={style.typeSelector}>
            <Mobile>
              {options}
            </Mobile>
            <section className={style.preview}>
              <FormattedMessage id="wishList.modal.preview.label">{
                text => <label>{text}</label>
              }</FormattedMessage>
              <div className={style.imageWrap}>
                <img src={wishListTypes[wishListTypeIndex].image}
                     className={style[`type-${wishListTypeIndex + 1}`]}
                     alt="Qempo"/>
              </div>
            </section>
            <Default>
              {options}
            </Default>
          </section>
          <section className={style.embed}>
            <FormattedMessage id="wishList.modal.embed.label">{
              text => <label>{text}:</label>
            }</FormattedMessage>
            <section className={style.embedCodeWrap}>
              <p>{iframe}</p>
            </section>
          </section>
          <FormattedMessage id={buttonCopyIframeIntl}>{
            text => <button className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                            onClick={_ => this.handleCopyIframe(iframe)}>{text}</button>
          }</FormattedMessage>
        </section>
      </Modal>);
  }
}

ShareListModal.propTypes = {
  openModal: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  localeState: PropTypes.object.isRequired,
  wishList: PropTypes.object,
};

ShareListModal.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/wish-list/components/ShareListModal/ShareListModal.jsx